import React, { useEffect, useMemo } from 'react';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { ReactComponent as AdWordIcon } from 'assets/adword.svg';
import classNames from 'classnames';
import adwordConnectImgSrc from 'assets/adword-connect.svg';
import { Button, Tooltip } from '@mm/components';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { LinkOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import {
  Grid,
  GridCell,
  GridLabels,
  GridRow,
} from '../../../../../../../../common/components/Grid/Grid';
import { TableLabel } from '../../../../../../../../common/components/TableGrid/TableGrid';
import {
  ComponentDataItem,
  DefaultCell,
  renderDataCell,
} from '../../../../../../contacts/components/ContactsTable/ContactTable';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BotBuilderRoutes } from '../../../../../../../../routes/bot-builder-routes';
import { useGoogleAdsCRUDActions } from '@mm/state/actions/GoogleAdsCRUDActions';
import {
  selectGoogleAdAudiences,
  selectGoogleClientCredentials,
} from '@mm/state/selectors/googleAdsSelectors';
import css from '../FacebookAdsIntegrations/FacebookAdsIntegrations.module.css';
import {
  FACEBOOK_ADS,
  GOOGLE_ADS,
  useConnectionsTableActions,
} from '@mm/state/modules/connectionsTableState/actions';
import { useViewActions } from '@mm/state/actions/viewActions';
import {
  CellProps,
  IntegrationEntity,
  renderActionsCell,
  renderAudienceCell,
  renderStatusCell,
} from '../FacebookAdsIntegrations/FacebookAdsIntegrations';
import { activeIntegrationSelector } from '@mm/state/modules/connectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import styles from '../../../../../../../components/SlideoutMenu/menus/GoogleAdsIntegrationEditorSlideout/GoogleAdsIntegrationEditorSlideout.module.css';
import { isNumber } from 'lodash';
import { StoreState } from '@mm/state/store-interfaces';
import { landmineNames } from '@mm/constants/landmine-names';
import { Help } from '../KlaviyoIntegrations/KlaviyoHelpLink';

export const renderAdAccountCell = (item: IntegrationEntity) => (
  <GridCell className="ad-account-cell">
    {item.googleClientCredential
      ? `${item.googleClientCredential.clientId} (${item?.googleClientCredential?.user?.email})`
      : ''}
  </GridCell>
);

export const renderAudienceSizeCell = ({ item }: CellProps) => (
  <GridCell>
    {isNumber(item.remoteData?.sizeForDisplay) &&
    item.remoteData?.sizeForDisplay
      ? item.remoteData.sizeForDisplay
      : '0 - 1000'}
  </GridCell>
);

const generateTableCells = (
  editSheet: (id: number) => void,
  deleteConnection: (item: IntegrationEntity) => void,
  onChangeOrder: (orderBy: string) => void,
) => {
  const cells: ComponentDataItem[] = [
    {
      label: 'Google Ad Account',
      keyName: 'google_ad_account',
      onChange: onChangeOrder,
      Cell: ({ item }: CellProps) => renderAdAccountCell(item),
    },
    {
      label: 'Audience',
      keyName: 'audience',
      onChange: onChangeOrder,
      Cell: renderAudienceCell,
    },
    {
      label: 'Google Ad Audience',
      keyName: 'google_ads_audience',
      onChange: onChangeOrder,
      Cell: renderDataCell('name'),
    },
    {
      label: 'Audience Size',
      keyName: 'audience_size',
      onChange: onChangeOrder,
      Cell: renderAudienceSizeCell,
    },
    {
      label: 'Sync to Google Ads',
      keyName: 'sync_automatically',
      onChange: onChangeOrder,
      Cell: renderStatusCell,
    },
    {
      label: 'Actions',
      keyName: 'actions',
      Cell: ({ item }: CellProps) =>
        renderActionsCell(item, editSheet, deleteConnection),
    },
  ];
  return cells;
};

const ConnectGoogleAccount = ({
  className,
  onClick,
}: {
  className?: string;
  onClick?: () => void;
}) => {
  const history = useHistory();
  const chatbotId = useSelector(activeChatbotIdSelector);

  return (
    <PrimaryButton className={classNames(className)} onClick={onClick}>
      Connect Google Account
    </PrimaryButton>
  );
};

const name = GOOGLE_ADS;

export const GoogleAdsIntegrations = ({ section }: { section?: any }) => {
  const fromIntegrations = !!section;

  const entities = useSelector(selectGoogleAdAudiences);
  const { setConnectionsOrderBy, setActiveIntegration } =
    useConnectionsTableActions();

  const history = useHistory();

  const chatbotId = useSelector(activeChatbotIdSelector);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const activeIntegration = useSelector(activeIntegrationSelector);
  const googleClientCredentials = useSelector(selectGoogleClientCredentials);

  const googleAdsCRUDActions = useGoogleAdsCRUDActions();
  const { openDeleteConfirmModal, openNewBillingModal2023 } = useModalActions();
  const subscriptionFeatures = useSelector(
    (state: StoreState) => state.featureFlags.subscriptionFeatureFlags,
  );

  const { showSlideoutMenu } = useViewActions();

  const sectionProps = { landmineName: landmineNames.INTEGRATIONS_GOOGLE_ADS };

  const setActiveIntegrationHandler = () => {
    if (activeIntegration === FACEBOOK_ADS) return;
    setActiveIntegration(FACEBOOK_ADS);
  };

  const onEdit = (id: number) => {
    setActiveIntegrationHandler();
    showSlideoutMenu('googleAdsIntegrationEditor', {
      adAudienceId: id,
    });
  };

  const onDelete = (item: IntegrationEntity) => {
    const deleteCallback = () => {
      googleAdsCRUDActions.deleteAudienceById(facebookPageId, item.id);
    };

    openDeleteConfirmModal({
      headerText: `Are you sure you want to delete ${item.name}?`,
      deleteCallback,
      newStyle: true,
    });
  };

  const onChangeOrder = orderBy => {
    setConnectionsOrderBy(orderBy);
  };

  const cellData = generateTableCells(onEdit, onDelete, onChangeOrder);

  const gridTemplateColumns = `20% 20% 20% 15% 15% 10%`;

  const count = entities.length;

  const addNew = async () => {
    showSlideoutMenu('googleAdsIntegrationEditor');
  };

  const onGoogleAccountConnect = () => {
    if (!subscriptionFeatures.includes('fbRestore')) {
      return openNewBillingModal2023(sectionProps);
    }
    history.push(BotBuilderRoutes.googleAds.getLink({ chatbotId }), {
      from: fromIntegrations ? 'integrations' : '',
    });
  };

  const onClickAddNew = () => {
    if (!subscriptionFeatures.includes('fbRestore')) {
      return openNewBillingModal2023(sectionProps);
    }
    addNew();
    setActiveIntegration(name);
  };

  const hasGoogleAdsAccount = useMemo(
    () => !!googleClientCredentials.length,
    [googleClientCredentials],
  );

  const headerComponent = (
    <div className="integrations__header-actions">
      <Help
        link="https://customers.ai/help/l/en/category/59r3s5it3b-google-ads"
        text="Help Center"
      />
      {!hasGoogleAdsAccount ? (
        <ConnectGoogleAccount
          className="integrations__header-add-button-secondary"
          onClick={onGoogleAccountConnect}
        />
      ) : (
        <div className={css.headerActions}>
          <div className={css.headerStatus}>
            <div className="integrations__header-status">
              <LinkOutlined className="integrations__header-status-icon" />
              <div className="integrations__header-status-count">{count}</div>
              Synced Audiences
            </div>
            <PrimaryButton
              className="integrations__header-add-button-secondary"
              onClick={e => {
                e?.stopPropagation();
                onClickAddNew();
              }}
              color={ButtonColor.secondaryBlue}
            >
              Create Google Ads Audience
            </PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );

  const fetchCredentials = async () => {
    if (facebookPageId) {
      googleAdsCRUDActions.retrieveGoogleClientCredentials(facebookPageId);
    }
  };

  const fetchAudiences = async () => {
    if (facebookPageId) {
      googleAdsCRUDActions.retrieveAll(facebookPageId);
    }
  };

  useEffect(() => {
    fetchCredentials();
    fetchAudiences();
  }, [facebookPageId]);

  return (
    <div className={css.wrapper}>
      <BaseIntegration
        openOnMount={window?.location?.href?.includes('restore')}
        integrationType="google_ads_audience"
        titleComponent={
          <BaseIntegrationHeader
            title={
              <>
                <span>Google Ads</span>
                <span
                  className={styles.link}
                  onClick={e => {
                    e.preventDefault();
                    onGoogleAccountConnect();
                  }}
                >
                  Configure
                </span>
              </>
            }
            icon={<AdWordIcon />}
            headerRightComponent={headerComponent}
            css={{
              container: classNames(
                css.headerAccordion,
                'integrations__header-container',
              ),
              icon: 'integrations__header-icon-adword',
            }}
            {...sectionProps}
          />
        }
      >
        {!hasGoogleAdsAccount ? (
          <div className={css.empty}>
            <div className={css.iconContainer}>
              <img
                className={css.icon}
                src={adwordConnectImgSrc}
                alt="Connect Google Account"
              />
            </div>

            <ConnectGoogleAccount
              onClick={onGoogleAccountConnect}
              className={css.connectBtn}
            />
          </div>
        ) : (
          <div className={css.container}>
            <div className={css.header}>
              <span className={css.title}>
                Google Ads Audience Sync
                <Tooltip
                  title="Send your website visitor data to Google Ads"
                  placement="bottom"
                >
                  <QuestionCircleOutlined className={css.infoIcon} />
                </Tooltip>
              </span>
              <div className={css.actions}>
                <div className={css.countInfo}>
                  Synced Audiences
                  <LinkOutlined className={css.countIcon} />
                  <div className={css.countText}>{entities.length}</div>
                </div>

                <Button
                  css={{ container: css.actionBtn }}
                  color={ButtonColor.secondaryBlue}
                  onClick={onClickAddNew}
                >
                  Create Google Ads Audience
                </Button>
              </div>
            </div>

            {!entities.length ? (
              <div className={css.empty}>
                <div className={css.iconContainer}>
                  <img
                    className={css.icon}
                    src={adwordConnectImgSrc}
                    alt="Google Adword"
                  />
                </div>
                <Button
                  css={{ container: css.ctaBtn }}
                  color={ButtonColor.blue}
                  onClick={onClickAddNew}
                >
                  Create Google Ads Audience
                </Button>
              </div>
            ) : (
              <Grid className={classNames(css.table, 'integrations-grid')}>
                <>
                  <GridLabels
                    className={css.tableHeader}
                    gridTemplateColumns={gridTemplateColumns}
                  >
                    {cellData.map(cell => (
                      <TableLabel
                        key={(cell.keyName as string) || cell.label?.toString()}
                        dataItem={cell}
                        orderByAtTheEnd
                      />
                    ))}
                  </GridLabels>

                  <div className={css.tableRows}>
                    {entities.map(item => (
                      <GridRow
                        key={item.id}
                        gridTemplateColumns={gridTemplateColumns}
                      >
                        {cellData.map(({ Cell, keyName }) =>
                          Cell ? (
                            <Cell key={keyName} item={item} />
                          ) : (
                            <DefaultCell
                              key={keyName}
                              keyName={keyName as string}
                              item={item}
                            />
                          ),
                        )}
                      </GridRow>
                    ))}
                  </div>
                </>
              </Grid>
            )}
          </div>
        )}
      </BaseIntegration>
    </div>
  );
};
